/**
 * Endpoints Configuration
 * Control which API endpoints are enabled/disabled
 */

// Environment-based endpoint configuration
const ENDPOINTS_CONFIG = {
  // Enable/disable specific endpoints
  dashboard: {
    metrics: false, // Disabled until endpoint is ready
    orders: true, // Enabled for recent orders
    customers: false, // Disabled until endpoint is ready
    users: true, // Enabled for users management
  },

  // Reports endpoints
  reports: {
    orders: true, // Enabled for today's orders
    sales: true, // Enabled for today's sales
    orders_accept_spend: true, // Enabled for time spent to accept orders
    arrived_pickup_spend: true, // Enabled for time spent waiting for ready for pickup
    customer_satisfaction: false, // Enabled for customer satisfaction ratings
    users: true, // Enabled for today's users
    top_selling: true, // Enabled for top selling products
    top_categories: false, // Enabled for top categories
    executive: true, // Enabled for executive monthly report PDF download
    performance: true, // Enabled for performance report PDF download
  },

  orders: {
    messages: true, // Enabled for order messages
  },

  // User endpoints (usually work)
  user: {
    profile: false, // Disabled - we use store data instead of API
    update: true, // Usually works
    sessions: true, // GET /users/${user.id}/sessions
    revokeSession: true, // DELETE /users/${user.id}/sessions/${session.id}
    revokeAllSessions: true, // DELETE /users/${user.id}/sessions/all
  },

  // Business endpoints
  business: {
    list: true, // GET /business - fetch all businesses
    detail: true, // GET /business/:id - fetch specific business details
    toggle: true, // POST /business/:id - toggle enabled status
  },

  // Third party integrations endpoints
  thirdPartyIntegrations: {
    list: true, // GET /third_party_integrations - fetch all integrations
  },

  // Logs endpoints
  logs: {
    list: true, // GET /logs - fetch integration logs
    detail: true, // GET /logs/:id - fetch specific log details
  },

  // AI Agents endpoints
  "ai-agents": {
    list: true, // GET /ai/agents - fetch all AI agents
    get: true, // GET /ai/agents/:id - fetch specific agent
    create: true, // POST /ai/agents - create new agent
    update: true, // PUT /ai/agents/:id - update agent
    schemas: true, // GET /ai/agents/provider_settings - get provider schemas
  },

  // Franchises endpoints
  franchises: {
    list: true, // GET /franchises - fetch all franchises
  },

  // configs
  configs: {
    list: true, // GET /configs - fetch all configs
  },

  // Config categories endpoints
  "config-categories": {
    list: true, // GET /config_categories - fetch config categories
  },

  // AI Conversations endpoints
  ai: {
    conversations: true, // GET /ai/conversations - fetch all conversations
    conversation: true, // GET /ai/conversations/:id - fetch specific conversation
    messages: true, // GET /ai/conversations/:id/messages - fetch conversation messages
    metrics: true, // GET /ai/metrics/overview - fetch AI metrics overview
    ordersMetrics: true, // GET /ai/metrics/orders - fetch AI orders metrics
    chatsMetrics: true, // GET /ai/metrics/chats - fetch AI chats metrics
    productsMetrics: true, // GET /ai/metrics/products - fetch AI products metrics
    agentsMetrics: true, // GET /ai/metrics/agents - fetch AI agents metrics
    performanceMetrics: true, // GET /ai/metrics/performance - fetch AI performance metrics
  },

  // Other endpoints
  auth: {
    login: true, // Usually works
    logout: true, // Usually works
    me: true, // Usually works
  },
};

/**
 * Check if an endpoint is enabled
 * @param {string} category - Category of endpoint (e.g., 'dashboard', 'user')
 * @param {string} endpoint - Specific endpoint (e.g., 'metrics', 'profile')
 * @returns {boolean} - Whether the endpoint is enabled
 */
export const isEndpointEnabled = (category, endpoint) => {
  return ENDPOINTS_CONFIG[category]?.[endpoint] ?? false;
};

/**
 * Get all enabled endpoints for a category
 * @param {string} category - Category of endpoints
 * @returns {Object} - Object with enabled endpoints
 */
export const getEnabledEndpoints = (category) => {
  return ENDPOINTS_CONFIG[category] || {};
};

/**
 * Enable/disable an endpoint at runtime
 * @param {string} category - Category of endpoint
 * @param {string} endpoint - Specific endpoint
 * @param {boolean} enabled - Whether to enable or disable
 */
export const setEndpointEnabled = (category, endpoint, enabled) => {
  if (!ENDPOINTS_CONFIG[category]) {
    ENDPOINTS_CONFIG[category] = {};
  }
  ENDPOINTS_CONFIG[category][endpoint] = enabled;
};

export default ENDPOINTS_CONFIG;
